import { OrderHistoryDateCardSelection } from 'common/src/models/order.enums';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BreadcrumbItem } from '../../../../cf-ui/Breadcrumbs/CfBreadcrumbs';
import { CfDataGridData, CfDataGridDataRequest, CfDataGridProps } from '../../../../cf-ui/DataGrid/CfDataGridProps';
import { CfDataGridContainer } from '../../../../cf-ui/DataGridContainer/CfDataGridContainer';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import { cfFreezeNames } from '../../../../theme/theme';
import { createTableViewDefinitions } from './DiaryGrid.table';
import DiaryGridHeader from './DiaryGridHeader';
import DiaryRevertDialog from './DiaryRevertDialog';

export type DiaryGridHistory = {
  dateSelection?: OrderHistoryDateCardSelection;
  startDate?: string;
  endDate?: string;
  breadcrumbs?: BreadcrumbItem[];
};

export type DiaryGridParams = {
  filter?: string;
};

export type DiaryGridItem = {
  id: string;
  action: string;
  summary: string;
  changeBy: string;
  logDate: Date | string;
  canRevert?: boolean;
};

type DiaryGridProps = {
  gridId: string;
  title: string;
  isLoading: boolean;
  gridData: DiaryGridItem[];
  onRevert?: (id: string, onSuccess?: () => void) => void;
  revertDialogType?: 'recipe' | 'ingredient' | 'menu';
};

const dates = {
  currDate: new Date(),
};

export default function DiaryGrid(props: DiaryGridProps): JSX.Element {
  const { isLoading, gridData, gridId, title, onRevert, revertDialogType } = props;

  const history = useHistory<DiaryGridHistory>();
  const { filter } = useQueryParams<DiaryGridParams>();

  const [revertId, setRevertId] = useState('');
  const [filterInputContainer, setFilterInputContainer] = useState<HTMLElement | null>(null);
  const filterInputContainerRef = useCallback((element: HTMLElement | null) => {
    setFilterInputContainer(element);
  }, []);

  const handleFilter = (request: CfDataGridDataRequest) => {
    const currentParams = new URLSearchParams(history.location.search);

    if ((request.filterText || '') !== (filter || '')) {
      if (request.filterText) {
        currentParams.set('filter', request.filterText);
      } else {
        currentParams.delete('filter');
      }

      history.replace(history.location.pathname + '?' + currentParams.toString(), history.location?.state);
    }
  };

  const handleDateRangeChange = (_startDate: Date, _endDate: Date, selectionOption: OrderHistoryDateCardSelection) => {
    const currentParams = new URLSearchParams(history.location.search);

    history.replace(history.location.pathname + '?' + currentParams.toString(), {
      ...(history.location.state || {}),
      dateSelection: selectionOption,
      startDate: _startDate.toISOString(),
      endDate: _endDate.toISOString(),
    });
  };

  const clearRevertId = () => {
    setRevertId('');
  };

  const data: CfDataGridData<DiaryGridItem> = {
    pages: [
      {
        isLoading,
        items: gridData,
        pageIndex: 0,
        pageSize: gridData.length,
      },
    ],
    totalPageCount: 1,
    totalItemCount: gridData.length,
    pagingMode: 'none',
    filterText: filter ?? '',
    filterPlaceholderText: 'Search diary',
  };

  const dataGridProps: CfDataGridProps<DiaryGridItem> = {
    data,
    id: gridId,
    selectId: (item) => item.id,
    componentContainers: { filterInputContainer },
    tableView: createTableViewDefinitions(onRevert && setRevertId),
    onDataRequested: handleFilter,
    noItemsMessage: 'The diary is empty.',
    autoFocus: 'filter',
    filterInputSx: {
      width: { lg: 168, xl: 182 },
    },
  };

  return (
    <>
      <CfDataGridContainer
        dataGridProps={dataGridProps}
        headerClassName={cfFreezeNames.contentHeader}
        customControlsContainer={
          <DiaryGridHeader
            filterInputContainerRef={filterInputContainerRef}
            title={title}
            startDate={new Date(history.location?.state?.startDate ?? dates.currDate)}
            endDate={new Date(history.location?.state?.endDate ?? dates.currDate)}
            dateSelection={history.location.state?.dateSelection ?? OrderHistoryDateCardSelection.Last7Days}
            onDateRangeChange={handleDateRangeChange}
          />
        }
      />
      {revertId && onRevert && revertDialogType && (
        <DiaryRevertDialog
          onClose={clearRevertId}
          onSubmit={() => onRevert(revertId, clearRevertId)}
          type={revertDialogType}
        />
      )}
    </>
  );
}
